import React from 'react';

const beginJaar = 2012;
const eindJaar = 2026;
const years = Array.from({ length: eindJaar - beginJaar + 1 }, (_, i) => beginJaar + i);
const EUREKA_BASE_URL = typeof window !== 'undefined' && window.EUREKA_PLUGIN_URL
  ? window.EUREKA_PLUGIN_URL
  : null;
const UPLOADS_URL = EUREKA_BASE_URL
  ? `${EUREKA_BASE_URL}uitslagen-output/`
  : '/wp-content/uploads/2025/07/';

function useUitslagenData() {
  const [data, setData] = React.useState({});
  const [meta, setMeta] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // Debug logging alleen als ?test in URL staat
    const isDebugMode = typeof window !== 'undefined' && window.location.search.includes('test');
    // eslint-disable-next-line no-console
    const debugLog = isDebugMode ? console.log : () => {};

    debugLog('useUitslagenData hook called');
    debugLog('useEffect triggered - starting fetchAll');
    let cancelled = false;
    async function fetchAll() {
      setLoading(true);
      const newData = {};
      const newMeta = {};
      const newErrors = {};
      await Promise.all(years.map(async (jaar) => {
        try {
          const fetchUrl = `${UPLOADS_URL}results-${jaar}.json`;

          const res = await fetch(fetchUrl);
          if (!res.ok) throw new Error('Bestand niet gevonden of niet leesbaar.');
          const json = await res.json();
          // json: { ...meta, uitslagen: [...] }
          newData[jaar] = Array.isArray(json.uitslagen) ? json.uitslagen : [];
          // Haal alle meta velden behalve 'uitslagen' zelf
          const { uitslagen, ...metaFields } = json;
          newMeta[jaar] = metaFields;
        } catch (e) {
          newData[jaar] = [];
          newMeta[jaar] = null;
          newErrors[jaar] = 'Bestand niet gevonden of niet leesbaar.';
        }
      }));
      if (!cancelled) {
        setData(newData);
        setMeta(newMeta);
        setErrors(newErrors);
        setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, []);
  return {
    uitslagenData: data, uitslagenMeta: meta, uitslagenErrors: errors, loading,
  };
}

// Parst tijdnotaties: hh:mm:ss, mm:ss, of m:ss
function parseTime(t) {
  if (!t) return null;
  const parts = t.toString().trim().split(':');
  if (parts.length === 3) {
    return Number(parts[0]) * 3600 + Number(parts[1]) * 60 + Number(parts[2]);
  }
  if (parts.length === 2) {
    return Number(parts[0]) * 60 + Number(parts[1]);
  }
  return null;
}

function isInvalidTime(r) {
  const t = (r.totaaltijd || '').toString().toLowerCase().trim();
  if (!t || t === '.' || t === '-') return true;
  if (t === 'dnf' || t === 'dns' || t === 'dq') return true;
  // Controleer ook het plaats-veld op DNF/DNS
  const p = (r.plaats || '').toString().toLowerCase().trim();
  if (p === 'dnf' || p === 'dns' || p === 'dq') return true;
  return parseTime(t) === null;
}

function sortRows(rows) {
  if (!Array.isArray(rows)) return rows;
  const typePriority = {
    nk: 0,
    dubbel: 1,
    enkel: 2,
  };
  const parseSortTime = (value) => {
    if (value == null) return null;
    const normalized = String(value).trim();
    if (!normalized) return null;
    const parts = normalized.split(':').map((p) => Number(p));
    if (!parts.every((p) => Number.isFinite(p))) return null;
    if (parts.length === 2) {
      const [mm, ss] = parts;
      if (ss < 0 || ss > 59 || mm < 0) return null;
      return mm * 60 + ss;
    }
    if (parts.length === 3) {
      const [hh, mm, ss] = parts;
      if (mm < 0 || mm > 59 || ss < 0 || ss > 59 || hh < 0) return null;
      return hh * 3600 + mm * 60 + ss;
    }
    return null;
  };

  const isNonFinish = (row) => {
    const statusTokens = [
      (row.plaats || ''),
      (row.totaaltijd || ''),
      (row.status || ''),
    ]
      .map((v) => String(v).trim().toLowerCase());
    return statusTokens.some((v) => ['dnf', 'dns', 'dsq', 'dq'].includes(v));
  };

  return [...rows].sort((a, b) => {
    const aInvalid = isInvalidTime(a);
    const bInvalid = isInvalidTime(b);
    if (aInvalid && !bInvalid) return 1;
    if (!aInvalid && bInvalid) return -1;
    if (aInvalid && bInvalid) return 0;
    const aTypePriority = typePriority[a.type] ?? Number.MAX_SAFE_INTEGER;
    const bTypePriority = typePriority[b.type] ?? Number.MAX_SAFE_INTEGER;
    if (aTypePriority !== bTypePriority) {
      return aTypePriority - bTypePriority;
    }
    const timeA = parseSortTime(a.totaaltijd);
    const timeB = parseSortTime(b.totaaltijd);
    return timeA - timeB;
  });
}

function filterRows(rows, searchTerm) {
  const normalizedSearch = (searchTerm || '').trim().toLowerCase();
  if (!normalizedSearch) return sortRows(rows);
  const lower = normalizedSearch;
  return sortRows(rows.filter((row) => {
    const naam = row.naam ? row.naam.toString().toLowerCase() : '';
    const partner = row.partner ? row.partner.toString().toLowerCase() : '';
    const team = row.team ? row.team.toString().toLowerCase() : '';
    const woonplaats = row.woonplaats ? row.woonplaats.toString().toLowerCase() : '';
    const startnr = row.startnummer ? row.startnummer.toString().toLowerCase() : '';
    return naam.includes(lower)
      || partner.includes(lower)
      || team.includes(lower)
      || woonplaats.includes(lower)
      || startnr.includes(lower);
  }));
}

function collapseNkTeamRows(rows) {
  if (!Array.isArray(rows)) return rows;
  const merged = [];
  const nkByKey = new Map();
  rows.forEach((row) => {
    if (row.type !== 'nk') {
      merged.push(row);
      return;
    }

    const key = [
      row.jaar ?? '',
      row.onderdeel ?? '',
      row.startnummer ?? '',
      row.plaats ?? '',
      row.totaaltijd ?? '',
      row.team ?? '',
    ].join('__');

    if (!nkByKey.has(key)) {
      const nkRow = { ...row };
      nkByKey.set(key, nkRow);
      merged.push(nkRow);
      return;
    }

    const existing = nkByKey.get(key);
    if (!existing.partner && row.naam) existing.partner = row.naam;
    if (!existing.team && row.team) existing.team = row.team;
    if (!existing.naam && row.naam) existing.naam = row.naam;
  });
  return merged;
}

export { useUitslagenData, sortRows, filterRows, collapseNkTeamRows, years };
